import type { PullRequestReaction, PullRequestReactionContent } from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";

const REACTIONS: ReadonlyArray<readonly [PullRequestReactionContent, string]> = [
  ["thumbs-up", "👍"],
  ["thumbs-down", "👎"],
  ["laugh", "😄"],
  ["hooray", "🎉"],
  ["confused", "😕"],
  ["heart", "❤️"],
  ["eyes", "👀"],
  ["rocket", "🚀"],
];

export function PullRequestReactionPills({
  reactions,
  enabled,
  pending = false,
  onToggle,
}: {
  readonly reactions?: ReadonlyArray<PullRequestReaction> | undefined;
  readonly enabled: boolean;
  readonly pending?: boolean;
  readonly onToggle: (content: PullRequestReactionContent, reacted: boolean) => void;
}) {
  const visibleReactions = enabled
    ? REACTIONS
    : REACTIONS.filter(([content]) =>
        reactions?.some((candidate) => candidate.content === content && candidate.count > 0),
      );

  return visibleReactions.length > 0 ? (
    <div className="mt-2 flex flex-wrap gap-1">
      {visibleReactions.map(([content, emoji]) => (
        <PullRequestReactionPill
          key={content}
          content={content}
          emoji={emoji}
          reaction={reactions?.find((candidate) => candidate.content === content)}
          enabled={enabled}
          pending={pending}
          onToggle={onToggle}
        />
      ))}
    </div>
  ) : null;
}

function PullRequestReactionPill({
  content,
  emoji,
  reaction,
  enabled,
  pending,
  onToggle,
}: {
  readonly content: PullRequestReactionContent;
  readonly emoji: string;
  readonly reaction: PullRequestReaction | undefined;
  readonly enabled: boolean;
  readonly pending: boolean;
  readonly onToggle: (content: PullRequestReactionContent, reacted: boolean) => void;
}) {
  const presentation = reactionPresentation(reaction);
  return (
    <Button
      type="button"
      size="sm"
      variant={presentation.viewerHasReacted ? "secondary" : "outline"}
      disabled={!enabled || pending}
      aria-label={`${presentation.actionLabel} ${content} reaction`}
      onClick={enabled ? () => onToggle(content, !presentation.viewerHasReacted) : undefined}
    >
      {emoji}
      {presentation.countLabel}
    </Button>
  );
}

function reactionPresentation(reaction: PullRequestReaction | undefined) {
  const viewerHasReacted = reaction !== undefined && reaction.viewerHasReacted;
  return {
    viewerHasReacted,
    actionLabel: viewerHasReacted ? "Remove" : "Add",
    countLabel: reaction === undefined ? "" : ` ${reaction.count}`,
  };
}
